(() => {
  'use strict';

  const DRAWER_SELECTOR = '[data-rv-cart-drawer]';
  const RESPONSE_SELECTOR = '[data-rv-cart-drawer-response]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const normaliseLocalPath = (value, fallback = '/') => {
    try {
      const url = new URL(value || fallback, window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return `${url.pathname}${url.search}`;
    } catch (_error) {
      return fallback;
    }
  };

  const ajaxChangePath = (value, root) => {
    const fallback = `${root}cart/change.js`;
    const localPath = normaliseLocalPath(value, fallback);
    return localPath.endsWith('.js') ? localPath : `${localPath}.js`;
  };

  class RvCartDrawer {
    constructor(dialog) {
      this.dialog = dialog;
      this.content = dialog.querySelector('[data-rv-cart-drawer-content]');
      this.closeButton = dialog.querySelector('[data-rv-cart-drawer-close]');
      this.status = dialog.querySelector('[data-rv-cart-drawer-status]');
      this.message = dialog.querySelector('[data-rv-cart-drawer-message]');
      this.sectionId = dialog.dataset.rvCartSectionId || '';
      const root = window.Shopify?.routes?.root || '/';
      this.cartUrl = normaliseLocalPath(dialog.dataset.rvCartUrl, `${root}cart`);
      this.changeUrl = ajaxChangePath(dialog.dataset.rvCartChangeUrl, root);
      this.activeOpener = null;
      this.restoreFocusOnClose = true;
      this.mutationInFlight = false;
      this.mutationLease = null;
      this.operationSequence = 0;

      this.ready = Boolean(
        this.content
        && this.closeButton
        && this.status
        && this.message
        && this.sectionId
        && window.fetch
        && window.DOMParser
        && typeof dialog.showModal === 'function'
      );

      if (!this.ready) return;

      dialog.addEventListener('click', (event) => this.onDialogClick(event));
      dialog.addEventListener('keydown', (event) => this.onDialogKeydown(event));
      dialog.addEventListener('close', () => this.onClose());
      dialog.addEventListener('cancel', () => this.setOpenState(false));
      dialog.dataset.rvCartDrawerReady = 'true';
      this.updateOpeners(this.currentResponse());
    }

    isReady() {
      return this.ready && this.dialog.isConnected;
    }

    currentResponse() {
      return this.content?.querySelector(`:scope > ${RESPONSE_SELECTOR}`) || null;
    }

    getCount() {
      const value = Number(this.currentResponse()?.dataset.cartCount);
      return Number.isInteger(value) && value >= 0 ? value : null;
    }

    getMessage(name) {
      const messages = {
        updating: this.dialog.dataset.rvCartLabelUpdating,
        updated: this.dialog.dataset.rvCartLabelUpdated,
        review: this.dialog.dataset.rvCartLabelReview,
        refreshFailed: this.dialog.dataset.rvCartLabelRefreshFailed,
        viewBag: this.dialog.dataset.rvCartLabelViewBag
      };
      return messages[name] || '';
    }

    getRequestData() {
      if (!this.isReady()) return null;
      return {
        sectionId: this.sectionId,
        sectionsUrl: this.cartUrl
      };
    }

    beginOperation() {
      this.operationSequence += 1;
      return this.operationSequence;
    }

    isCurrentOperation(operationToken) {
      return this.isReady() && operationToken === this.operationSequence;
    }

    staleReconcileResult() {
      return {
        hydrated: false,
        count: null,
        countIncreased: false,
        stale: true
      };
    }

    beginMutation() {
      if (!this.isReady() || this.mutationInFlight) return null;
      const lease = Object.freeze({ operationToken: this.beginOperation() });
      this.mutationLease = lease;
      this.setBusy(true);
      this.announce(this.getMessage('updating'), { operationToken: lease.operationToken });
      return lease;
    }

    ownsMutation(lease) {
      return Boolean(
        lease
        && lease === this.mutationLease
        && this.mutationInFlight
        && this.isCurrentOperation(lease.operationToken)
      );
    }

    endMutation(lease) {
      if (lease !== this.mutationLease) return false;
      this.mutationLease = null;
      this.setBusy(false);
      return true;
    }

    invalidate() {
      this.operationSequence += 1;
      this.mutationLease = null;
      this.setBusy(false);
      this.ready = false;
    }

    setOpenState(isOpen) {
      document.body.classList.toggle('rv-cart-drawer-open', isOpen);
      document.querySelectorAll('[data-rv-cart-open]').forEach((opener) => {
        opener.setAttribute('aria-expanded', String(isOpen));
      });
    }

    open(opener = null, operationToken = null) {
      if (
        !this.isReady()
        || (operationToken !== null && !this.isCurrentOperation(operationToken))
      ) {
        return false;
      }
      if (opener instanceof HTMLElement && opener.isConnected) this.activeOpener = opener;

      if (!this.dialog.open) {
        try {
          this.dialog.showModal();
        } catch (_error) {
          return false;
        }
      }

      this.setOpenState(true);
      window.requestAnimationFrame(() => {
        if (operationToken !== null && !this.isCurrentOperation(operationToken)) return;
        this.closeButton?.focus({ preventScroll: true });
      });
      return true;
    }

    close({ restoreFocus = true } = {}) {
      if (!this.dialog.open) return;
      this.restoreFocusOnClose = restoreFocus;
      this.dialog.close();
    }

    onClose() {
      this.setOpenState(false);
      const opener = this.activeOpener;
      this.activeOpener = null;
      if (this.restoreFocusOnClose && opener?.isConnected) opener.focus({ preventScroll: true });
      this.restoreFocusOnClose = true;
    }

    focusableElements() {
      return [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
        return !element.hidden && element.getClientRects().length > 0;
      });
    }

    onDialogKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    onDialogClick(event) {
      const contentLink = event.target.closest('a[href]');
      if (
        contentLink
        && this.dialog.contains(contentLink)
        && !contentLink.hasAttribute('data-rv-cart-remove')
        && !contentLink.hasAttribute('data-rv-cart-view-bag')
      ) {
        this.restoreFocusOnClose = false;
      }

      if (event.target === this.dialog) {
        this.close();
        return;
      }

      if (event.target.closest('[data-rv-cart-drawer-close]')) {
        this.close();
        return;
      }

      const control = event.target.closest(
        '[data-rv-cart-increase], [data-rv-cart-decrease], [data-rv-cart-remove]'
      );
      if (!control || !this.dialog.contains(control)) return;

      if (control.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
        return;
      }

      event.preventDefault();
      if (this.mutationInFlight) return;

      const line = control.closest('[data-rv-cart-line]');
      const lineKey = line?.dataset.lineKey;
      if (!line || !lineKey) return;

      const currentQuantity = Number(line.querySelector('[data-rv-cart-quantity]')?.textContent);
      let nextQuantity = 0;
      if (control.matches('[data-rv-cart-increase]')) {
        if (!Number.isInteger(currentQuantity) || currentQuantity < 1) return;
        nextQuantity = currentQuantity + 1;
      } else if (control.matches('[data-rv-cart-decrease]')) {
        if (!Number.isInteger(currentQuantity) || currentQuantity < 1) return;
        nextQuantity = Math.max(0, currentQuantity - 1);
      }

      const action = control.hasAttribute('data-rv-cart-increase')
        ? 'increase'
        : control.hasAttribute('data-rv-cart-decrease')
          ? 'decrease'
          : 'remove';
      const focusSnapshot = {
        action,
        lineKey,
        lineIndex: Number(line.dataset.lineIndex) || 0
      };
      void this.changeLine(lineKey, nextQuantity, focusSnapshot);
    }

    setBusy(isBusy) {
      this.mutationInFlight = isBusy;
      this.dialog.dataset.rvBusy = String(isBusy);
      this.dialog.setAttribute('aria-busy', String(isBusy));
      this.dialog.querySelectorAll(
        '[data-rv-cart-increase], [data-rv-cart-decrease], [data-rv-cart-remove], [data-rv-cart-checkout]'
      ).forEach((control) => {
        if ('disabled' in control) control.disabled = isBusy;
        control.setAttribute('aria-disabled', String(isBusy));
      });
    }

    announce(message, { alert = false, operationToken = null } = {}) {
      if (
        !this.status
        || (operationToken !== null && !this.isCurrentOperation(operationToken))
      ) {
        return;
      }
      this.status.setAttribute('role', alert ? 'alert' : 'status');
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        if (operationToken !== null && !this.isCurrentOperation(operationToken)) return;
        this.status.textContent = message || '';
      });
    }

    validateResponse(response) {
      if (!response || response.dataset.rvCartSectionId !== this.sectionId) return false;

      const count = Number(response.dataset.cartCount);
      const isEmpty = response.dataset.cartEmpty;
      const isSellable = response.dataset.cartSellable;
      const lineCount = Number(response.dataset.cartLineCount);
      const checkoutCount = Number(response.dataset.cartCheckoutCount);
      const cartLabel = response.dataset.cartLabel?.trim() || '';
      if (!Number.isInteger(count) || count < 0) return false;
      if (!Number.isInteger(lineCount) || lineCount < 0) return false;
      if (!Number.isInteger(checkoutCount) || ![0, 1].includes(checkoutCount)) return false;
      if (!cartLabel || count < lineCount) return false;
      if (!['true', 'false'].includes(isEmpty) || !['true', 'false'].includes(isSellable)) return false;
      if ((count === 0) !== (isEmpty === 'true')) return false;
      if ((lineCount === 0) !== (isEmpty === 'true')) return false;
      if ((checkoutCount === 1) !== (isSellable === 'true')) return false;

      const checkoutControls = response.querySelectorAll('[data-rv-cart-checkout]');
      const checkoutButtons = response.querySelectorAll('[name="checkout"]');
      const checkoutLinks = [...response.querySelectorAll('a[href]')].filter((link) => {
        try {
          return new URL(link.getAttribute('href'), window.location.origin).pathname.endsWith('/checkout');
        } catch (_error) {
          return true;
        }
      });
      const checkoutEntrypointCount = checkoutButtons.length + checkoutLinks.length;
      if (isSellable === 'true') {
        if (
          isEmpty === 'true'
          || checkoutControls.length !== checkoutCount
          || checkoutEntrypointCount !== checkoutCount
        ) {
          return false;
        }
      } else if (checkoutControls.length !== 0 || checkoutEntrypointCount !== 0) {
        return false;
      }

      const lineKeys = [...response.querySelectorAll('[data-rv-cart-line]')].map(
        (line) => line.dataset.lineKey || ''
      );
      if (lineKeys.length !== lineCount) return false;
      if (lineKeys.some((key) => !key) || new Set(lineKeys).size !== lineKeys.length) return false;
      return true;
    }

    parseSectionHTML(sectionHTML) {
      if (typeof sectionHTML !== 'string' || !sectionHTML.trim()) return null;
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const sectionRoots = [...parsed.querySelectorAll('[data-rv-cart-drawer-section]')].filter(
        (root) => root.dataset.rvCartSectionId === this.sectionId
      );
      const responses = [...parsed.querySelectorAll(RESPONSE_SELECTOR)];
      if (sectionRoots.length !== 1 || responses.length !== 1) return null;
      const response = responses[0];
      if (!sectionRoots[0].contains(response) || !this.validateResponse(response)) return null;
      return response;
    }

    updateOpeners(response) {
      if (!response) return;
      const count = Number(response.dataset.cartCount);
      const label = response.dataset.cartLabel || '';
      if (!Number.isInteger(count) || count < 0 || !label) return;

      document.querySelectorAll('[data-rv-cart-open]').forEach((opener) => {
        opener.setAttribute('aria-label', label);
        let countNode = opener.querySelector('.rv-cart-count');
        if (!countNode && count > 0) {
          countNode = document.createElement('span');
          countNode.className = 'rv-cart-count';
          countNode.setAttribute('aria-hidden', 'true');
          opener.append(countNode);
        }
        if (countNode) {
          countNode.textContent = String(count);
          countNode.hidden = count === 0;
        }
      });
    }

    captureFocus() {
      const active = this.dialog.contains(document.activeElement) ? document.activeElement : null;
      const line = active?.closest('[data-rv-cart-line]');
      if (!active || !line) return null;
      return {
        action: active.hasAttribute('data-rv-cart-increase')
          ? 'increase'
          : active.hasAttribute('data-rv-cart-decrease')
            ? 'decrease'
            : active.hasAttribute('data-rv-cart-remove')
              ? 'remove'
              : '',
        lineKey: line.dataset.lineKey || '',
        lineIndex: Number(line.dataset.lineIndex) || 0
      };
    }

    restoreMutationFocus(snapshot, operationToken = null) {
      if (
        !snapshot
        || !this.dialog.open
        || (operationToken !== null && !this.isCurrentOperation(operationToken))
      ) {
        return;
      }
      const lines = [...this.dialog.querySelectorAll('[data-rv-cart-line]')];
      let line = lines.find((candidate) => candidate.dataset.lineKey === snapshot.lineKey);
      if (!line) line = lines[Math.min(snapshot.lineIndex, Math.max(0, lines.length - 1))];

      const actionSelector = snapshot.action ? `[data-rv-cart-${snapshot.action}]` : '';
      const target = (actionSelector && line?.querySelector(actionSelector))
        || line?.querySelector('[data-rv-cart-decrease], [data-rv-cart-remove], a[href], button')
        || this.closeButton;
      window.requestAnimationFrame(() => {
        if (operationToken !== null && !this.isCurrentOperation(operationToken)) return;
        target?.focus({ preventScroll: true });
      });
    }

    hydrateSectionHTML(sectionHTML, { focusSnapshot = null, operationToken } = {}) {
      if (!this.isCurrentOperation(operationToken)) return false;
      const response = this.parseSectionHTML(sectionHTML);
      if (!response || !this.isCurrentOperation(operationToken)) return false;
      const imported = document.importNode(response, true);
      this.content.replaceChildren(imported);
      this.dialog.removeAttribute('data-rv-cart-unconfirmed');
      this.message.hidden = true;
      this.message.textContent = '';
      this.updateOpeners(imported);
      this.restoreMutationFocus(focusSnapshot, operationToken);
      document.dispatchEvent(new CustomEvent('rv:cart:updated', {
        detail: { itemCount: Number(imported.dataset.cartCount), source: 'section' }
      }));
      return true;
    }

    sectionRequestUrl() {
      const url = new URL(this.cartUrl, window.location.origin);
      url.searchParams.set('sections', this.sectionId);
      return `${url.pathname}${url.search}`;
    }

    async refreshForOperation(
      operationToken,
      { open = false, opener = null, focusSnapshot = null } = {}
    ) {
      if (!this.isCurrentOperation(operationToken)) return false;
      try {
        const response = await fetch(this.sectionRequestUrl(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
          credentials: 'same-origin'
        });
        if (!this.isCurrentOperation(operationToken)) return false;
        const result = await response.json().catch(() => ({}));
        if (!this.isCurrentOperation(operationToken)) return false;
        const hydrated = response.ok
          && this.hydrateSectionHTML(result?.[this.sectionId], {
            focusSnapshot,
            operationToken
          });
        if (!this.isCurrentOperation(operationToken)) return false;
        if (hydrated && open) this.open(opener, operationToken);
        return hydrated;
      } catch (_error) {
        return false;
      }
    }

    refresh(options = {}) {
      if (!this.isReady() || this.mutationInFlight) return Promise.resolve(false);
      const operationToken = this.beginOperation();
      return this.refreshForOperation(operationToken, options);
    }

    markUnconfirmed(operationToken) {
      if (!this.isCurrentOperation(operationToken)) return false;
      const recovery = document.createElement('div');
      recovery.className = 'rv-cart-drawer__recovery';
      recovery.dataset.rvCartDrawerRecovery = '';

      const message = document.createElement('p');
      message.setAttribute('role', 'alert');
      message.textContent = this.getMessage('refreshFailed');

      const viewBag = document.createElement('a');
      viewBag.className = 'rv-button rv-button--primary';
      viewBag.dataset.rvCartViewBag = '';
      viewBag.href = this.cartUrl;
      viewBag.textContent = this.getMessage('viewBag') || 'VIEW BAG';

      recovery.append(message, viewBag);
      this.content.replaceChildren(recovery);
      this.dialog.dataset.rvCartUnconfirmed = 'true';
      this.message.textContent = '';
      this.message.hidden = true;
      document.querySelectorAll('[data-rv-cart-open]').forEach((opener) => {
        const countNode = opener.querySelector('.rv-cart-count');
        if (countNode) {
          countNode.textContent = '';
          countNode.hidden = true;
        }
        opener.setAttribute('aria-label', this.getMessage('viewBag') || 'VIEW BAG');
      });
      this.announce(this.getMessage('refreshFailed'), { alert: true, operationToken });
      return true;
    }

    async reconcileForOperation(operationToken, {
      sectionHTML = null,
      responseOk = true,
      previousCount = null,
      open = false,
      opener = null,
      focusSnapshot = null
    } = {}) {
      if (!this.isCurrentOperation(operationToken)) return this.staleReconcileResult();
      let hydrated = false;
      if (responseOk) {
        hydrated = this.hydrateSectionHTML(sectionHTML, { focusSnapshot, operationToken });
      }
      if (!hydrated) {
        hydrated = await this.refreshForOperation(operationToken, { focusSnapshot });
      }
      if (!this.isCurrentOperation(operationToken)) return this.staleReconcileResult();

      const count = this.getCount();
      const countIncreased = Number.isInteger(previousCount)
        && Number.isInteger(count)
        && count > previousCount;

      if (hydrated) {
        this.announce(responseOk ? this.getMessage('updated') : this.getMessage('review'), {
          operationToken
        });
        if (open || (!responseOk && countIncreased)) this.open(opener, operationToken);
      } else {
        this.markUnconfirmed(operationToken);
        if (open) this.open(opener, operationToken);
      }

      return { hydrated, count, countIncreased, stale: false };
    }

    reconcile(options = {}) {
      if (!this.isReady() || this.mutationInFlight) {
        return Promise.resolve(this.staleReconcileResult());
      }
      const operationToken = this.beginOperation();
      return this.reconcileForOperation(operationToken, options);
    }

    reconcileMutation(lease, options = {}) {
      if (!this.ownsMutation(lease)) {
        return Promise.resolve(this.staleReconcileResult());
      }
      return this.reconcileForOperation(lease.operationToken, options);
    }

    async changeLine(lineKey, quantity, focusSnapshot) {
      const lease = this.beginMutation();
      if (!lease) return;
      const { operationToken } = lease;

      try {
        const response = await fetch(this.changeUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: String(lineKey),
            quantity,
            sections: this.sectionId,
            sections_url: this.cartUrl
          })
        });
        if (!this.isCurrentOperation(operationToken)) return;
        const result = await response.json().catch(() => ({}));
        if (!this.isCurrentOperation(operationToken)) return;
        await this.reconcileForOperation(operationToken, {
          sectionHTML: result?.sections?.[this.sectionId],
          responseOk: response.ok,
          focusSnapshot
        });
      } catch (_error) {
        if (!this.isCurrentOperation(operationToken)) return;
        const hydrated = await this.refreshForOperation(operationToken, { focusSnapshot });
        if (!this.isCurrentOperation(operationToken)) return;
        if (hydrated) this.announce(this.getMessage('review'), { operationToken });
        else this.markUnconfirmed(operationToken);
      } finally {
        this.endMutation(lease);
      }
    }
  }

  let instance = null;

  const initialise = () => {
    const dialog = document.querySelector(DRAWER_SELECTOR);
    if (!dialog) {
      instance = null;
      return null;
    }
    if (!instance || instance.dialog !== dialog) instance = new RvCartDrawer(dialog);
    else {
      instance.updateOpeners(instance.currentResponse());
      instance.setOpenState(dialog.open);
    }
    return instance;
  };

  const getInstance = () => instance?.isReady() ? instance : initialise();

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-rv-cart-open]');
    if (!opener || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = getInstance();
    if (drawer?.open(opener)) event.preventDefault();
  });

  document.addEventListener('shopify:section:load', () => initialise());
  document.addEventListener('shopify:section:unload', (event) => {
    if (instance && event.target.contains(instance.dialog)) {
      instance.invalidate();
      instance.close({ restoreFocus: false });
      instance = null;
    }
  });

  window.RonniesCartDrawer = {
    get ready() {
      return Boolean(getInstance()?.isReady());
    },
    getRequestData() {
      return getInstance()?.getRequestData() || null;
    },
    getCount() {
      return getInstance()?.getCount() ?? null;
    },
    getMessage(name) {
      return getInstance()?.getMessage(name) || '';
    },
    open(opener = null) {
      return getInstance()?.open(opener) || false;
    },
    beginMutation() {
      return getInstance()?.beginMutation() || null;
    },
    endMutation(lease) {
      return getInstance()?.endMutation(lease) || false;
    },
    reconcile(options = {}) {
      const drawer = getInstance();
      return drawer
        ? drawer.reconcile(options)
        : Promise.resolve({ hydrated: false, count: null, countIncreased: false, stale: true });
    },
    reconcileMutation(lease, options = {}) {
      const drawer = getInstance();
      return drawer
        ? drawer.reconcileMutation(lease, options)
        : Promise.resolve({ hydrated: false, count: null, countIncreased: false, stale: true });
    },
    refresh(options = {}) {
      return getInstance()?.refresh(options) || Promise.resolve(false);
    }
  };

  initialise();
})();
